import { previewExtensions } from "@editor/Extensions";
import { GetEditorExtensions } from "../Extensions";
import { getSchema } from "@tiptap/core";
import type { Transaction } from "@tiptap/pm/state";
import { DOMParser as PMDOMParser } from "@tiptap/pm/model";
import { NodeSelection, TextSelection } from "@tiptap/pm/state";
import { generateJSON, generateText, type Editor, type NodeWithPos } from "@tiptap/react";

export const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const MAC_SYMBOLS: Record<string, string> = {
    mod: "⌘",
    command: "⌘",
    meta: "⌘",
    ctrl: "⌃",
    control: "⌃",
    alt: "⌥",
    option: "⌥",
    shift: "⇧",
    backspace: "Del",
    delete: "⌦",
    enter: "⏎",
    escape: "⎋",
    capslock: "⇪",
} as const;

export const SR_ONLY = {
    position: "absolute",
    width: "1px",
    height: "1px",
    padding: 0,
    margin: "-1px",
    overflow: "hidden",
    clip: "rect(0, 0, 0, 0)",
    whiteSpace: "nowrap",
    borderWidth: 0,
} as const;

export function cn(...classes: (string | boolean | undefined | null)[]): string {
    return classes.filter(Boolean).join(" ");
}

/**
 * Determines if the current platform is macOS
 * @returns boolean indicating if the current platform is Mac
 */
export function isMac(): boolean {
    return typeof navigator !== "undefined" && navigator.platform.toLowerCase().includes("mac");
}

/**
 * Formats a shortcut key based on the platform (Mac or non-Mac)
 * @param key - The key to format (e.g., "ctrl", "alt", "shift")
 * @param isMac - Boolean indicating if the platform is Mac
 * @param capitalize - Whether to capitalize the key (default: true)
 * @returns Formatted shortcut key symbol
 */
export const formatShortcutKey = (key: string, isMac: boolean, capitalize: boolean = true) => {
    if (isMac) {
        const lowerKey = key.toLowerCase();
        return MAC_SYMBOLS[lowerKey] || (capitalize ? key.toUpperCase() : key);
    }

    return capitalize ? key.charAt(0).toUpperCase() + key.slice(1) : key;
};

/**
 * Parses a shortcut key string into an array of formatted key symbols
 * @param shortcutKeys - The string of shortcut keys (e.g., "ctrl-alt-shift")
 * @param delimiter - The delimiter used to split the keys (default: "-")
 * @param capitalize - Whether to capitalize the keys (default: true)
 * @returns Array of formatted shortcut key symbols
 */
export const parseShortcutKeys = (props: { shortcutKeys: string | undefined; delimiter?: string; capitalize?: boolean }) => {
    const { shortcutKeys, delimiter = "+", capitalize = true } = props;

    if (!shortcutKeys) return [];

    return shortcutKeys
        .split(delimiter)
        .map(key => key.trim())
        .map(key => formatShortcutKey(key, isMac(), capitalize));
};

/**
 * Checks if a mark exists in the editor schema
 * @param markName - The name of the mark to check
 * @param editor - The editor instance
 * @returns boolean indicating if the mark exists in the schema
 */
export const isMarkInSchema = (markName: string, editor: Editor | null): boolean => {
    if (!editor?.schema) return false;
    return editor.schema.spec.marks.get(markName) !== undefined;
};

/**
 * Checks if a value is a valid number (not null, undefined, or NaN)
 * @param value - The value to check
 * @returns boolean indicating if the value is a valid number
 */
export function isValidPosition(pos: number | null | undefined): pos is number {
    return typeof pos === "number" && pos >= 0;
}

/**
 * Checks if one or more extensions are registered in the Tiptap editor.
 * @param editor - The Tiptap editor instance
 * @param extensionNames - A single extension name or an array of names to check
 * @returns True if at least one of the extensions is available, false otherwise
 */
export function isExtensionAvailable(editor: Editor | null, extensionNames: string | string[]): boolean {
    if (!editor) return false;

    const names = Array.isArray(extensionNames) ? extensionNames : [extensionNames];

    const found = names.some(name => editor.extensionManager.extensions.some(ext => ext.name === name));

    if (!found) {
        console.warn(
            `None of the extensions [${names.join(", ")}] were found in the editor schema. Ensure they are included in the editor configuration.`,
        );
    }

    return found;
}

/**
 * Determines whether the current selection contains a node whose type matches
 * any of the provided node type names.
 * @param editor Tiptap editor instance
 * @param nodeTypeNames List of node type names to match against
 * @param checkAncestorNodes Whether to check ancestor node types up the depth chain
 */
export function isNodeTypeSelected(editor: Editor | null, nodeTypeNames: string[] = [], checkAncestorNodes: boolean = false): boolean {
    if (!editor || !editor.state.selection) return false;

    const { selection } = editor.state;
    if (selection.empty) return false;

    // Direct node selection check
    if (selection instanceof NodeSelection) {
        const selectedNode = selection.node;
        return selectedNode ? nodeTypeNames.includes(selectedNode.type.name) : false;
    }

    // Depth-based ancestor node check
    if (checkAncestorNodes) {
        const { $from } = selection;
        for (let depth = $from.depth; depth > 0; depth--) {
            const ancestorNode = $from.node(depth);
            if (nodeTypeNames.includes(ancestorNode.type.name)) {
                return true;
            }
        }
    }

    return false;
}

/**
 * Update a single attribute on multiple nodes.
 *
 * @param tr - The transaction to mutate
 * @param targets - Array of { node, pos }
 * @param attrName - Attribute key to update
 * @param next - New value OR updater function receiving previous value
 *               Pass `undefined` to remove the attribute.
 * @returns true if at least one node was updated, false otherwise
 */
export function updateNodesAttr<A extends string = string, V = unknown>(
    tr: Transaction,
    targets: readonly NodeWithPos[],
    attrName: A,
    next: V | ((prev: V | undefined) => V | undefined),
): boolean {
    if (!targets.length) return false;

    let changed = false;

    for (const { pos } of targets) {
        // Always re-read from the transaction's current doc
        const currentNode = tr.doc.nodeAt(pos);
        if (!currentNode) continue;

        const prevValue = (currentNode.attrs as Record<string, unknown>)[attrName] as V | undefined;
        const resolvedNext = typeof next === "function" ? (next as (p: V | undefined) => V | undefined)(prevValue) : next;

        if (prevValue === resolvedNext) continue;

        const nextAttrs: Record<string, unknown> = { ...currentNode.attrs };
        if (resolvedNext === undefined) {
            // Remove the key entirely instead of setting null
            delete nextAttrs[attrName];
        } else {
            nextAttrs[attrName] = resolvedNext;
        }

        tr.setNodeMarkup(pos, undefined, nextAttrs);
        changed = true;
    }

    return changed;
}

/**
 * Selects the entire content of the current block node if the selection is empty.
 * If the selection is not empty, it does nothing.
 * @param editor The Tiptap editor instance
 */
export function selectCurrentBlockContent(editor: Editor) {
    const { selection, doc } = editor.state;

    if (!selection.empty) return;

    const $pos = selection.$from;
    let blockNode: typeof doc | null = null;
    let blockPos = -1;

    for (let depth = $pos.depth; depth >= 0; depth--) {
        const node = $pos.node(depth);
        const pos = $pos.start(depth);

        if (node.isBlock && node.textContent.trim()) {
            blockNode = node;
            blockPos = pos;
            break;
        }
    }

    if (blockNode && blockPos >= 0) {
        const from = blockPos;
        const to = blockPos + blockNode.nodeSize - 2; // -2 to exclude the closing tag

        if (from < to) {
            const $from = doc.resolve(from);
            const $to = doc.resolve(to);
            const newSelection = TextSelection.between($from, $to, 1);

            if (newSelection && !selection.eq(newSelection)) {
                editor.view.dispatch(editor.state.tr.setSelection(newSelection));
            }
        }
    }
}

export function getPreviewTextFromMessage(message: string) {
    try {
        return generateText(generateJSON(message, previewExtensions), previewExtensions);
    } catch (e) {
        console.error(`Failed to generate preview text from communication message: ${message}`);
    }
}

/**
 * Recursively strips all `attrs` fields from Tiptap JSONContent.
 * Tiptap adds auto-generated attributes during initialization which causes
 * JSON comparisons to fail even when content is unchanged.
 */
export function stripTiptapAttrs(obj: any): any {
    if (Array.isArray(obj)) {
        return obj.map(stripTiptapAttrs);
    }
    if (obj && typeof obj === "object") {
        const result: any = {};
        for (const [key, value] of Object.entries(obj)) {
            if (key === "attrs") {
                // Skip attrs entirely
                continue;
            }
            result[key] = stripTiptapAttrs(value);
        }
        return result;
    }
    return obj;
}

export function htmlToTipTapJson(html: string): object {
    const schema = getSchema(GetEditorExtensions({ placeholder: "Type your message here", extensionOverrides: [] }));
    const dom = new DOMParser().parseFromString(`<!DOCTYPE html><html><body>${html}</body></html>`, "text/html");
    return PMDOMParser.fromSchema(schema).parse(dom.body).toJSON();
}
